import React, { useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { NETAPI_URL } from '../../config/api';
import { infiniteRetryPolicy, startResilientConnection } from '../../utils/signalRConnection';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
  Tooltip,
  styled,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentsIcon from '@mui/icons-material/Payments';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Snackbar, Alert, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { SuperAdminReminder } from '../../services/SuperAdminService';
import { ReminderActionsMenu, MutedRemindersDialog } from './ReminderMuteDialogs';
import {
  useSuperAdminReminders,
  reminderTiming,
  REMINDER_KIND_TEXTS,
} from '../../contexts/SuperAdminRemindersContext';
import type { SuperAdminReminderKind } from '../../services/SuperAdminService';
import { buildReminderHref } from './superAdminReminderLink';

/**
 * SuperAdminRemindersBell — süperadmin başlığındaki hatırlatma zili.
 *
 * Süresi dolmuş ve 30 gün içinde dolacak her kalemi (yazarkasa GMÜ süresi,
 * şirket sözleşmesi) ayrı satırda listeler. Liste canlı durumu gösterir: kalem
 * yenilenip bitiş tarihi ileri alınana kadar satır kaybolmaz, kapatılamaz
 * (kapatma yalnızca sağ alttaki bandı susturur).
 */

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#d32f2f',
    color: 'white',
  },
}));

const MenuHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

const KIND_ICONS: Record<SuperAdminReminderKind, React.ReactNode> = {
  FISCAL_DEVICE: <PointOfSaleIcon />,
  CONTRACT: <DescriptionIcon />,
  SUBSCRIPTION: <PaymentsIcon />,
  PRODUCT: <Inventory2Icon />,
  BILLING: <PaymentsIcon />,
  DEMO: <PaymentsIcon />,
};

const SuperAdminRemindersBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { reminders, totalCount, expiredCount, mutedCount, loading, refresh } = useSuperAdminReminders();
  const [liveNotice, setLiveNotice] = useState<string | null>(null);
  // Satır menüsü (ertele / dönem kapat / takibi bırak) ve susturulanlar penceresi
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; reminder: SuperAdminReminder } | null>(null);
  const [mutedOpen, setMutedOpen] = useState(false);
  const [toast, setToast] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const onMuteChanged = (text: string) => {
    setToast({ text, severity: 'success' });
    refresh();
  };

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${NETAPI_URL}/hubs/announcements`, { withCredentials: false })
      .withAutomaticReconnect(infiniteRetryPolicy)
      .build();
    connection.on('OnRenewalNotice', (payload: { message?: string }) => {
      if (payload?.message) setLiveNotice(payload.message);
      refresh();
    });
    // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
    // ürettiği için sunucudaki grup üyeliği kaybolur.
    const stopConnection = startResilientConnection(connection, {
      label: 'RenewalNotice',
      onConnected: () => connection.invoke('JoinOperators'),
    });
    return () => {
      stopConnection();
    };
  }, [refresh]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    refresh();
  };

  return (
    <>
      <Tooltip title="Yenileme hatırlatmaları">
        <IconButton color="inherit" onClick={handleOpen} sx={{ mr: 1 }}>
          <StyledBadge badgeContent={totalCount > 0 ? totalCount : null} max={99}>
            <NotificationsIcon />
          </StyledBadge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 460, maxHeight: 640, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        <MenuHeader>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Yenileme Hatırlatmaları
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalCount > 0
                ? `${totalCount} kayıt yenileme takibi gerektiriyor${expiredCount > 0 ? ` · ${expiredCount} tanesinin süresi doldu` : ''}`
                : 'Süresi dolan veya yaklaşan kayıt yok'}
            </Typography>
            {liveNotice && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {liveNotice}
              </Typography>
            )}
            {mutedCount > 0 && (
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => setMutedOpen(true)}
                sx={{ mt: 0.5, display: 'block' }}
              >
                Susturulanlar ({mutedCount})
              </Link>
            )}
          </Box>
          <Tooltip title="Yenile">
            <IconButton size="small" onClick={() => refresh()} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MenuHeader>

        <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
          {loading && reminders.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : reminders.length > 0 ? (
            <List disablePadding>
              {reminders.map((reminder) => {
                const expired = reminder.daysRemaining < 0;
                const texts = REMINDER_KIND_TEXTS[reminder.kind];
                const expiryText = reminder.expiryDate
                  ? new Date(reminder.expiryDate).toLocaleDateString('tr-TR')
                  : '';

                return (
                  <React.Fragment key={reminder.key}>
                    <ListItemButton
                      onClick={() => {
                        setAnchorEl(null);
                        navigate(buildReminderHref(reminder));
                      }}
                      sx={{
                        alignItems: 'flex-start',
                        borderLeft: '4px solid',
                        borderLeftColor: expired ? 'error.main' : 'warning.main',
                        py: 1.25,
                        pr: 6,
                        position: 'relative',
                      }}
                    >
                      <Tooltip title="Ertele / kapat">
                        <IconButton
                          size="small"
                          aria-label="Uyarı işlemleri"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRowMenu({ anchor: e.currentTarget, reminder });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: expired ? 'error.main' : 'warning.main', color: 'white' }}>
                          {KIND_ICONS[reminder.kind]}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                              {reminder.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={texts?.name ?? reminder.kind}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={expired ? 'Süresi doldu' : 'Yaklaşıyor'}
                                size="small"
                                color={expired ? 'error' : 'warning'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.75 }}>
                            {reminder.subtitle && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                                {reminder.subtitle}
                              </Typography>
                            )}
                            {reminder.detail && (
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                              >
                                {reminder.detail}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {expiryText
                                ? `${reminderTiming(reminder.daysRemaining)} · Bitiş: ${expiryText}`
                                : reminderTiming(reminder.daysRemaining)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>Bekleyen yenileme hatırlatması yok</Typography>
              <Typography variant="caption">
                GMÜ ve sözleşme 30 günde listelenir. Aboneliği 45 gün içinde bitecek ve tahsilatı olmayan şirketler de burada görünür.
              </Typography>
            </Box>
          )}
        </Box>
      </Menu>

      <ReminderActionsMenu
        anchorEl={rowMenu?.anchor ?? null}
        reminder={rowMenu?.reminder ?? null}
        onClose={() => setRowMenu(null)}
        onGo={(r) => {
          setAnchorEl(null);
          navigate(buildReminderHref(r));
        }}
        onChanged={onMuteChanged}
        onError={(text) => setToast({ text, severity: 'error' })}
      />
      <MutedRemindersDialog open={mutedOpen} onClose={() => setMutedOpen(false)} onChanged={onMuteChanged} />
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default SuperAdminRemindersBell;
